import {
  APP_VERSION,
  PRODUCT_META,
  ProductCode,
  ROLE_LABELS,
  RoleCode,
  userSalutationName,
  type AuthUser,
} from '@gb-ticket/shared-types';
import {
  BellIcon,
  Building2Icon,
  CalendarOffIcon,
  LayoutGridIcon,
  LogOutIcon,
  MonitorIcon,
  UserRoundIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/platform/components/user-avatar';
import { AbsenceNoteDialog } from '@/products/intranet/components/absence-note-dialog';
import { appLogoPath, hasIntranetProduct } from '@/platform/lib/post-login';
import { useLogout } from '@/features/auth/use-auth-mutations';
import { useVoice } from '@/platform/voice/use-voice';
import { ADMIN_ROOT, canOpenAdmin } from '@/platform/lib/admin';
import { NOTIFICATIONS_PATH, PROFILE_PATH } from '@/platform/lib/paths';

const ROLE_RANK = [
  RoleCode.IT_ADMIN,
  RoleCode.IT_LEAD,
  RoleCode.IT_AGENT,
  RoleCode.END_USER,
] as const;

function roleLabelOf(user: AuthUser): string | null {
  const ranked = ROLE_RANK.find((role) => user.roles.includes(role));
  return ranked ? ROLE_LABELS[ranked] : null;
}

export function UserMenu({
  user,
  showPortalLink = false,
  showAgentLink = false,
  showPortalHomeLink = false,
}: {
  user: AuthUser;
  showPortalLink?: boolean;
  showAgentLink?: boolean;
  showPortalHomeLink?: boolean;
}) {
  const voice = useVoice();
  const logout = useLogout();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const roleLabel = roleLabelOf(user);
  const helpdesk = PRODUCT_META[ProductCode.HELPDESK];
  const salutation = userSalutationName(user, voice);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="max-w-48 gap-2 px-2"
            aria-label={`Konto, ${salutation}`}
          >
            <span className="size-8 shrink-0 overflow-hidden rounded-full">
              <UserAvatar
                firstName={user.firstName}
                lastName={user.lastName}
                displayName={user.displayName}
                profilePhotoUrl={user.profilePhotoUrl}
              />
            </span>
            <span className="hidden truncate sm:inline">{salutation}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <span className="block truncate font-medium">{salutation}</span>
            {roleLabel ? (
              <span className="mt-0.5 block text-xs text-muted-foreground">{roleLabel}</span>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showPortalHomeLink ? (
            <DropdownMenuItem asChild>
              <Link to={appLogoPath(user)}>
                <LayoutGridIcon />
                Mitarbeiterportal
              </Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem asChild>
            <Link to={PROFILE_PATH}>
              <UserRoundIcon />
              Profil
            </Link>
          </DropdownMenuItem>
          {hasIntranetProduct(user) ? (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                setAbsenceOpen(true);
              }}
            >
              <CalendarOffIcon />
              Abwesenheitsnotiz
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem asChild>
            <Link to={NOTIFICATIONS_PATH}>
              <BellIcon />
              Mitteilungen
            </Link>
          </DropdownMenuItem>
          {canOpenAdmin(user) ? (
            <DropdownMenuItem asChild>
              <Link to={ADMIN_ROOT}>
                <Building2Icon />
                Verwaltung
              </Link>
            </DropdownMenuItem>
          ) : null}
          {showPortalLink ? (
            <DropdownMenuItem asChild>
              <Link to={helpdesk.selfServicePath ?? helpdesk.pathPrefix}>
                <MonitorIcon />
                Self-Service ansehen
              </Link>
            </DropdownMenuItem>
          ) : null}
          {showAgentLink ? (
            <DropdownMenuItem asChild>
              <Link to={`${helpdesk.pathPrefix}/tickets`}>
                <MonitorIcon />
                Zur IT-Oberfläche
              </Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={logout.isPending}
            onSelect={() => {
              void logout.mutateAsync();
            }}
          >
            <LogOutIcon />
            Abmelden
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            Version {APP_VERSION}
          </DropdownMenuLabel>
        </DropdownMenuContent>
      </DropdownMenu>
      {absenceOpen ? <AbsenceNoteDialog open onClose={() => setAbsenceOpen(false)} /> : null}
    </>
  );
}
